/**
 * Where the three beneficiary screens take their people from (DEV-7682, ST5 / AC 5).
 *
 * `02M-01-BENEFICIAIRE`, `02A-01-BENEFICIAIRE` and `02B-01-BENEFICIAIRE` each carried
 * their own copy of the same prefill cascade (saved answers → Parent Context contract),
 * including their own `Date` → `yyyy-MM-dd` conversion and their own "is this person the
 * policy holder?" heuristic. Three copies, one rule: the cascade now lives HERE and each
 * screen only maps the result into its own form shape.
 *
 * ## Nobody is ever invented
 *
 * The cascade has exactly TWO sources, both upstream, and NO third step. `02B` and `02A` used
 * to end theirs on a hardcoded pair of insured people — a holder, a spouse and a Montréal
 * address, all made up — whenever the app ran outside the parent app. The claimant could tick
 * them, and they travelled all the way into the NOVA file. They are gone, and so is the
 * per-screen flag that existed only to authorize them (DEV-7682, dev review of PR #241).
 * When neither source answers, the list stays EMPTY — see `isEmpty` below.
 * `screens/__tests__/beneficiaryInvention.spec.ts` keeps them out.
 *
 * ## The rule
 *
 * | `beneficiariesFromEligibility` | prefill source                                 |
 * |--------------------------------|------------------------------------------------|
 * | `true`                         | the eligibility response — and NOTHING else    |
 * | `false`                        | the Parent Context contract, exactly as before |
 *
 * The flag says WHOSE context this is (DEV-7491). A tenant served through the generic
 * portal shell gets its client context from NOVA, which names no insured people at all
 * (the BFF's NovaToParentContextMapper leaves `beneficiaries` unset) — the eligibility
 * response is the only list that can answer. The discriminant was `requireEventDate`
 * until DEV-7491: keying the source on the event-date requirement held only while every
 * shell-served tenant also REQUIRED a date, and iA (date OPTIONAL, people present in the
 * eligibility response) broke the coincidence — its screens read the always-empty Parent
 * Context and showed `noBeneficiariesMessage` to every insured.
 *
 * When the flag is ON there is deliberately NO fallback. The eligibility service already
 * filtered its participants (on the event date when the tenant requires one), so it is
 * the only list that answers the question the screen asks ("who was covered?"). Falling
 * back to the Parent Context on an empty answer would hide a failing eligibility behind a
 * list of people who may not have been covered at all — the exact defect AC 5 (DEV-7682)
 * forbids. An empty list is a real answer: the screens render
 * `steps.beneficiary.noBeneficiariesMessage`, which sends the claimant to customer
 * service, instead of quietly showing the wrong names.
 *
 * The `false` branch is NOT a fallback, it is the other tenant (PV, embedded in the
 * Espace client whose context DOES carry the insured people): it keeps the pre-DEV-7682
 * behaviour byte for byte, name-comparison included.
 *
 * ## Phase 1 only — evolution tracked by DEV-7867
 *
 * Exclusive sources are a phase-1 simplification: safe (no regression on the PV flow, no
 * unfiltered names resurfacing), but restrictive — each branch throws away whatever the
 * other source could have contributed. The intended evolution is a MERGE with priority:
 * Parent Context values win, and absent values (missing people, gender, province, role, …)
 * are filled from the eligibility response when it provides them. DEV-7867 carries that
 * work — matching people across the two lists without a stable upstream id, keeping AC 5
 * (never resurface someone eligibility did not cover), and retiring
 * `beneficiariesFromEligibility` once the merge replaces the exclusive choice.
 *
 * ## `policyKeeper`
 *
 * The BFF exposes the policy keeper as `contractRole === 'POLICY_HOLDER'`. That replaces the
 * `firstName + lastName === claimant's` comparison the screens used to run: two insured
 * people can share a name, a claimant can act for someone else, and the comparison silently
 * broke on accents/casing. The name comparison survives in ONE place only — deriving a role
 * for a Parent Context row that carries none (legacy contracts) — so the `false` branch keeps
 * behaving as it did.
 *
 * ## PII (ADR-039)
 *
 * Every field but `id` is PII. This module never logs, never caches and never persists a row:
 * it reads `claimStore.stepData`, which is in-memory only (`partialize` keeps `drafts` alone).
 *
 * ## Timing
 *
 * The flag is read from the tenant config, which `EmbeddedAppProvider` initializes during the
 * `CONTEXT_RESPONSE` handshake — sections `00` and `01` stand between that handshake and the
 * first beneficiary screen, so the config has always landed by the time a screen resolves its
 * prefill. `null`/missing config therefore means "no tenant at all" (standalone), not "not yet
 * loaded"; `getDefaultBrandingConfig` in `services/tenantService.ts` documents the value used
 * when the config call fails (it fails CLOSED, i.e. `beneficiariesFromEligibility: true`).
 */

import { ContractRole, Gender } from '../types';
import type { EligibilityBeneficiary, EligibilityVerifyResponse } from '../services/api/endpoints/eligibilityEndpoints';
import type { Beneficiary as ParentContextBeneficiary } from '../types/parentContext';
import type { TenantConfig } from '../types/tenant';
import { formatDateShort } from './dateUtils';
import { CLAIMANT_SCREEN_CODE } from './eventDateScreenVisibility';

/** Screen that stores the selected contract and its eligibility response. */
export const CONTRACT_SCREEN_CODE = '01-01-CONTRAT';

/** What the selected-contract screen writes, of which only these two keys matter here. */
export interface ContractStepData {
  eligibilityResponse?: EligibilityVerifyResponse | null;
  contractDetails?: {
    originalContract?: { beneficiaries?: ParentContextBeneficiary[] | null } | null;
  } | null;
}

/** The claimant's identity, used only to derive a missing Parent Context role. */
export interface ClaimantStepData {
  firstName?: string;
  lastName?: string;
}

/**
 * Minimal shape read out of `claimStore.stepData`.
 * Loose on the sibling keys (the store itself is `Record<string, any>`), typed on the two
 * this module owns — same convention as `utils/eventDateScreenVisibility.ts`.
 */
export interface BeneficiaryStepData {
  [screenCode: string]: unknown;
  '01-01-CONTRAT'?: ContractStepData;
  '01-00-DEMANDEUR'?: ClaimantStepData;
}

/** Which list the screen was prefilled from. */
export type BeneficiarySourceKind = 'ELIGIBILITY' | 'PARENT_CONTEXT' | 'NONE';

/**
 * A prefill row, normalized across both sources so the screens map ONE shape.
 * `''` — never `undefined` — is used for "unknown", because every consumer feeds these
 * straight into controlled inputs.
 */
export interface PrefilledBeneficiary {
  /**
   * Row key. Always the positional `ben-N` on the eligibility branch (that source carries no
   * identifier); the Parent Context id when the host app supplied one, positional otherwise.
   */
  id: string;
  firstName: string;
  lastName: string;
  /** ISO `yyyy-MM-dd`. */
  dateOfBirth: string;
  gender: Gender | '';
  /**
   * `true` ⇒ the source stated a definite gender (`M` or `F`) and the screens lock the field on
   * it, so what NOVA receives is what the insurer holds (DEV-7886). `false` ⇒ the gender is
   * missing, unknown or `X`: the claimant picks it.
   */
  genderLocked: boolean;
  contractRole: string;
  provinceOrState: string;
  /**
   * Health-insurance number. Always `''` on the eligibility branch: the number does not
   * exist upstream, so it stays a manual entry on `02M-01-BENEFICIAIRE` (DEV-7448).
   */
  healthCard: string;
  /** `contractRole === 'POLICY_HOLDER'` — the BFF's `policyKeeper`. */
  policyKeeper: boolean;
}

export interface BeneficiaryPrefill {
  source: BeneficiarySourceKind;
  beneficiaries: PrefilledBeneficiary[];
  /** `true` ⇒ eligibility is the only admissible source; the Parent Context is not read. */
  eligibilityIsAuthoritative: boolean;
  /**
   * `true` ⇒ neither source named anyone, and the screen shows
   * `steps.beneficiary.noBeneficiariesMessage` over an empty list. There is no third source
   * to try: an empty list is the answer, not a gap to fill.
   */
  isEmpty: boolean;
}

/**
 * Do this tenant's beneficiary screens take their people from the eligibility response?
 *
 * The single reader of `features.beneficiariesFromEligibility`. Strict `=== true` so a
 * config that predates the flag (or no tenant at all) reads as "off" rather than
 * truthy-by-accident.
 */
export const isEligibilityBeneficiarySource = (
  tenantConfig: TenantConfig | null | undefined
): boolean => tenantConfig?.features?.beneficiariesFromEligibility === true;

/** The BFF's `policyKeeper`, expressed as it reaches the front end. */
export const isPolicyKeeper = (contractRole: string | null | undefined): boolean =>
  contractRole === ContractRole.POLICY_HOLDER;

/** Genders the forms accept; anything else (incl. `X` on the medical flow) becomes a prompt. */
const toGender = (gender: string | null | undefined): Gender | '' =>
  gender === Gender.MALE || gender === Gender.FEMALE || gender === Gender.OTHER ? gender : '';

/**
 * Only a definite gender locks the field. An unknown or missing one (the BFF sends `null` for
 * the insurer's `UNKNOWN`) and `X` stay editable: NOVA treats them as "no gender" everywhere
 * downstream (RAMQ refund, letters, health-insurance-number check), so the claimant supplies it.
 */
const isDefiniteGender = (gender: Gender | ''): boolean =>
  gender === Gender.MALE || gender === Gender.FEMALE;

/** The eligibility participants of the selected contract, date-filtered by the BFF. */
export const getEligibilityBeneficiaries = (
  stepData: BeneficiaryStepData | null | undefined
): EligibilityBeneficiary[] => stepData?.[CONTRACT_SCREEN_CODE]?.eligibilityResponse?.beneficiaries ?? [];

/** The insured people the host app attached to the selected contract. */
export const getParentContextBeneficiaries = (
  stepData: BeneficiaryStepData | null | undefined
): ParentContextBeneficiary[] =>
  stepData?.[CONTRACT_SCREEN_CODE]?.contractDetails?.originalContract?.beneficiaries ?? [];

/**
 * Eligibility row.
 *
 * ## The role comes from `contractRole`, and from nowhere else
 *
 * The BFF sets `contractRole` from the participant's `insuredType`, so it carries the real
 * contractual role for EVERYONE — `POLICY_HOLDER`, `SPOUSE`, `DEPENDENT_CHILD`, and
 * `GROUP_MEMBER` on a carrier that does not distinguish a spouse from a child. The response
 * used to repeat that same `insuredType` under a second key, `relationship`; the BFF has since
 * dropped it, and with it the fallback that read it. An eligibility row with no `contractRole`
 * therefore resolves to `''` — no second field is consulted, and NO name comparison is run here
 * (that heuristic lives on the Parent Context branch only, see `fromParentContext`).
 *
 * `policyKeeper` is derived from the SAME resolved role, so the flag and the screens' own
 * `isPolicyKeeper(row.contractRole)` can never disagree.
 *
 * ## The id is positional, on purpose
 *
 * Upstream provides no stable participant identifier — the field it used to send was null on
 * every observed row and has been dropped too — so the row is keyed by its POSITION in the
 * date-filtered list: `ben-1`, `ben-2`, …
 *
 * Do NOT replace this with an id derived from the person's data. On the Beneva test contract two
 * dependent children share a date of birth and have names that differ only by casing, so
 * `firstName + lastName + dateOfBirth` collides — and a collision is not cosmetic: the screens
 * key their form rows, their selection state and their DOM ids on this value, and
 * `ClaimToNovaMapper` on the BFF indexes three UUIDs per beneficiary by it. Two rows sharing a
 * key would silently merge into one participant. The index is the one thing guaranteed unique.
 */
const fromEligibility = (
  beneficiary: EligibilityBeneficiary,
  index: number
): PrefilledBeneficiary => {
  const contractRole = beneficiary.contractRole || '';
  const gender = toGender(beneficiary.gender);

  return {
    id: `ben-${index + 1}`,
    firstName: beneficiary.firstName || '',
    lastName: beneficiary.lastName || '',
    dateOfBirth: formatDateShort(beneficiary.dateOfBirth),
    gender,
    genderLocked: isDefiniteGender(gender),
    contractRole,
    provinceOrState: beneficiary.provinceOrState || '',
    // Absent upstream — the NAM is typed by the claimant, never prefilled from eligibility.
    healthCard: '',
    policyKeeper: isPolicyKeeper(contractRole),
  };
};

/**
 * Parent Context row. `contractRole` is read first, then `relationship`, and only when both
 * are empty is the claimant's name compared — the legacy heuristic, kept so the
 * Parent-Context-sourced tenant (PV) behaves exactly as before DEV-7682.
 */
const fromParentContext = (
  beneficiary: ParentContextBeneficiary,
  index: number,
  claimant: ClaimantStepData
): PrefilledBeneficiary => {
  let contractRole = beneficiary.contractRole || beneficiary.relationship || '';

  if (!contractRole) {
    const claimantFirstName = claimant.firstName?.toLowerCase().trim();
    const claimantLastName = claimant.lastName?.toLowerCase().trim();
    const firstName = (beneficiary.firstName || '').toLowerCase().trim();
    const lastName = (beneficiary.lastName || '').toLowerCase().trim();

    if (claimantFirstName && claimantLastName && firstName === claimantFirstName && lastName === claimantLastName) {
      contractRole = ContractRole.POLICY_HOLDER;
    }
  }

  const gender = toGender(beneficiary.gender);

  return {
    id: beneficiary.id || `ben-${index + 1}`,
    firstName: beneficiary.firstName || '',
    lastName: beneficiary.lastName || '',
    dateOfBirth: formatDateShort(beneficiary.dateOfBirth),
    gender,
    genderLocked: isDefiniteGender(gender),
    contractRole,
    provinceOrState: beneficiary.provinceOrState || beneficiary.province || '',
    healthCard: beneficiary.healthCard || '',
    policyKeeper: isPolicyKeeper(contractRole),
  };
};

/**
 * Resolve the prefill list for a beneficiary screen.
 *
 * Pure: reads `stepData`, mutates nothing, and — this is the AC — never mixes the two
 * sources. The caller keeps ownership of its own "saved answers win" step, which runs
 * BEFORE this one and is source-agnostic.
 */
export const resolveBeneficiaryPrefill = ({
  stepData,
  beneficiariesFromEligibility,
  isEmbedded,
}: {
  stepData: BeneficiaryStepData | null | undefined;
  beneficiariesFromEligibility: boolean;
  isEmbedded: boolean;
}): BeneficiaryPrefill => {
  if (beneficiariesFromEligibility) {
    const beneficiaries = getEligibilityBeneficiaries(stepData).map(fromEligibility);
    return {
      source: beneficiaries.length > 0 ? 'ELIGIBILITY' : 'NONE',
      beneficiaries,
      eligibilityIsAuthoritative: true,
      isEmpty: beneficiaries.length === 0,
    };
  }

  const claimant = stepData?.[CLAIMANT_SCREEN_CODE] ?? {};
  const contextBeneficiaries = isEmbedded ? getParentContextBeneficiaries(stepData) : [];

  if (contextBeneficiaries.length > 0) {
    const beneficiaries = contextBeneficiaries.map((beneficiary, index) =>
      fromParentContext(beneficiary, index, claimant)
    );
    return {
      source: 'PARENT_CONTEXT',
      beneficiaries,
      eligibilityIsAuthoritative: false,
      isEmpty: false,
    };
  }

  return {
    source: 'NONE',
    beneficiaries: [],
    eligibilityIsAuthoritative: false,
    isEmpty: true,
  };
};
